import { useContext, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledInputSelect } from '@components/controlled-input-select';
import { SelectItem } from '@components/controlled-input-select/src/controlled-input-select.type';
import { REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/src/utils';
import { ErrorMessage } from '@components/error/src/error-message';
import { IconPerson, IconUsers } from '@components/icons';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { SelectItemOption } from '@components/select-item-option';
import { SORT_ASC } from '@constants/global';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { ActiveChannel } from '@domain/channel';
import { useGetCompanyChannels } from '@hooks/company/use-get-company-channels';
import { useGetActiveChannels } from '@hooks/use-active-channels';
import { Divider } from '@tremor/react';

// Constants for holder types
export const SINGLE_HOLDER = 'single';
export const BULK_HOLDER = 'bulk';

export interface BulkHolderSelectTypeProps {
  onContinue: () => void;
  onSelectSingle: (passId?: string) => void;
  passId: string;
  passOptions?: SelectItem[];
}

export const BulkHolderSelectType = ({
  onContinue,
  onSelectSingle,
  passId,
  passOptions,
}: BulkHolderSelectTypeProps) => {
  const { t } = useTranslation();
  const {
    formState: { isValid },
    watch,
    setValue,
    register,
  } = useFormContext();
  const {
    companyState: { id: companyId },
    clubState: { id: clubId },
  } = useContext(CompanyClubContext);
  const [channelList, setChannelList] = useState<ActiveChannel[]>([]);

  const channelId = watch('channelId');
  const holderType = watch('holderType');
  const watchedPassId = (watch('passId') as string | undefined) ?? passId;
  const showPassSelector = !!passOptions && passOptions.length > 0;

  // Get company channels
  const { data: companyChannels } = useGetCompanyChannels({
    companyId,
    clubId,
    orderBy: 'name',
    sort: SORT_ASC,
    options: { enabled: !!companyId && !!clubId },
  });

  // Get pass-specific active channels (skip when using in-modal pass selector
  // — the wizard flow uses company-level channels without active-channel filtering)
  const { data: activeChannels } = useGetActiveChannels({
    offerId: watchedPassId,
    enabled: !!watchedPassId && !showPassSelector,
  });

  // Format and merge channels just like in pass-create-modal
  useEffect(() => {
    if (!companyChannels) return;

    const channelsFormatted = companyChannels.map((channel) => ({
      ...channel,
      maxQuantity: null,
    }));

    // If no activeChannels, use all formatted channels
    if (!activeChannels) {
      setChannelList(channelsFormatted);
      return;
    }

    // Filter channels based on active channels (same logic as pass-create-modal)
    const filteredChannels = channelsFormatted.filter((channel) => {
      const currentActiveChannel = activeChannels.channels.filter((element) => element.id === channel.id);
      return currentActiveChannel.length > 0 || activeChannels.isForAllChannels;
    });

    setChannelList(filteredChannels);
  }, [activeChannels, companyChannels]);

  const toggleHolderType = (type: typeof SINGLE_HOLDER | typeof BULK_HOLDER) => {
    if (holderType === type) return;
    setValue('holderType', type, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const typeOptions = useMemo(() => {
    return [
      {
        id: SINGLE_HOLDER,
        name: t('passes:singleHolderTitle'),
        subtitle: t('passes:singleHolderSubtitle'),
        bordered: true,
        icon: IconPerson,
        active: holderType === SINGLE_HOLDER,
        wrapperClasses: 'flex-1',
        action: () => toggleHolderType(SINGLE_HOLDER),
      },
      {
        id: BULK_HOLDER,
        name: t('passes:bulkHolderTitle'),
        subtitle: t('passes:bulkHolderSubtitle'),
        bordered: true,
        icon: IconUsers,
        active: holderType === BULK_HOLDER,
        wrapperClasses: 'flex-1',
        action: () => toggleHolderType(BULK_HOLDER),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, holderType]);

  const isFormValid = isValid && channelId && holderType;

  // Auto-pick first option for the in-modal pass selector to save the user a click;
  // user can still change.
  useEffect(() => {
    if (!showPassSelector || !passOptions) return;
    const formPassId = watch('passId');
    if (!formPassId && passOptions.length > 0) {
      setValue('passId', passOptions[0].id, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPassSelector, passOptions]);

  const handleContinue = () => {
    if (holderType === SINGLE_HOLDER) {
      onSelectSingle(watchedPassId);
    } else if (holderType === BULK_HOLDER) {
      onContinue();
    }
  };

  return (
    <>
      <ModalStickyHeader title={t('passes:addHolderModalTitle')} />
      <div className='flex flex-col gap-4 px-4 pb-6 sm:px-20 sm:pb-12'>
        {showPassSelector && passOptions && (
          <section>
            <ControlledInputSelect
              fieldRegisterName='passId'
              options={passOptions}
              rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
              label={t('offers:offlineSalesPassSelectorLabel')}
            />
          </section>
        )}
        <section>
          <ControlledInputSelect
            fieldRegisterName='channelId'
            options={channelList}
            rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
            label={t('passes:selectChannelId')}
          />
        </section>

        {channelId && (
          <>
            <Divider className='my-0' />
            <section className='mb-2'>
              <fieldset
                className='flex items-stretch gap-4'
                {...register('holderType', {
                  required: holderType !== '' || t(REQUIRED_TEXT_ERROR),
                })}>
                {typeOptions.map((option) => (
                  <SelectItemOption {...option} key={option.id} />
                ))}
              </fieldset>
              <ErrorMessage name='holderType' />
            </section>
          </>
        )}
      </div>
      <ModalStickyFooter
        onPrimaryButtonClick={handleContinue}
        isSubmitDisabled={!isFormValid}
        primaryButtonText={t('offers:continueButton')}
      />
    </>
  );
};
